import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import { type CollabLockKind, collabLockKey } from "./collab";

/**
 * studioPanelSchema names the two floating studio panels that can be open — the
 * layers picker and the preferences picker. Zod-first because the open-panel
 * value is shared with the panel-toggle tool input; only one panel is open at a
 * time (the open-panel state is this value or null).
 */
export const studioPanelSchema = z.enum(["layers", "preferences"]);

/**
 * profileSheetSnapSchema names how far the docked elevation profile is opened on a
 * compact viewport, where it renders as a drag-to-resize bottom sheet rather than a
 * fixed dock: `peek` shows the characteristics strip alone, `half` adds the
 * elevation chart, `full` adds the surface + way-type analysis bars.
 *
 * It exists because the desktop profile reveals its analysis on HOVER, which a
 * touch device has none of — the snap is the touch-reachable replacement for that
 * reveal, and it is the user's to choose rather than something a height heuristic
 * takes away. Zod-first because the value is shared with the sheet tool's input.
 */
export const profileSheetSnapSchema = z.enum(["peek", "half", "full"]);

/**
 * ProfileSheetSnap is how far the compact elevation sheet is opened, derived from
 * profileSheetSnapSchema.
 */
export type ProfileSheetSnap = z.infer<typeof profileSheetSnapSchema>;

/**
 * mapAlertSchema is one map-level alert: a severity level (info, warning,
 * error) plus a 280-char human-readable message. Zod-first because the value
 * is shared with the alert-show tool input and validated when an agent or a
 * client surface raises an alert. Only one alert is shown at a time — a new
 * one replaces whichever was visible — so the alert slot holds this or null
 * (no alert visible).
 */
export const mapAlertSchema = z.object({
  level: z.enum(["info", "warning", "error"]),
  message: z.string().min(1).max(280),
});

/**
 * MapAlert is the visible map alert, derived from mapAlertSchema. The alert
 * slot holds this or null (the map has no alert visible).
 */
export type MapAlert = z.infer<typeof mapAlertSchema>;

/**
 * StudioPanel names a floating studio panel, derived from studioPanelSchema.
 */
export type StudioPanel = z.infer<typeof studioPanelSchema>;

/**
 * streetviewPositionSchema is the [lng, lat] map coordinate a Street View
 * panorama opens at. Zod-first because it is shared with the street-view-open
 * and street-view-move tool inputs and validated when an agent supplies a drop
 * point or as the Maps SDK reports the walker's new position. lat is bounded
 * to [-90, 90] and lng to [-180, 180] — the WGS84 envelope — so an out-of-range
 * input is rejected at the boundary rather than landing in the store.
 */
export const streetviewPositionSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

/**
 * StreetviewPosition is the coordinate a Street View panorama opens at, derived
 * from streetviewPositionSchema.
 */
export type StreetviewPosition = z.infer<typeof streetviewPositionSchema>;

/**
 * contextMenuPoiSchema is one point-of-interest the right-click context menu
 * shows under the cursor: a display `name` (null when the tile carries none) and
 * a `category`. It mirrors the map package's PoiInfo — the map produces the
 * value and the context-menu tool sets it here. It is re-declared rather than
 * imported because core is a leaf (it cannot depend on the map package); a
 * compatibility test pins the two shapes together so they cannot drift.
 */
const contextMenuPoiSchema = z.object({
  category: z.string(),
  name: z.string().nullable(),
});

/**
 * contextMenuOverlayFeatureSchema is one active-overlay vector feature the
 * right-click context menu shows under the cursor: a flat map of the feature's
 * display properties (e.g. a weather overlay's temperature, wind). Each value is
 * a primitive the popup renders as text, or null when the tile carries the key
 * with no value. It mirrors the map package's OverlayFeature — the map produces
 * the value and the context-menu tool sets it here. It is re-declared rather
 * than imported because core is a leaf (it cannot depend on the map package); a
 * compatibility test pins the two shapes together so they cannot drift.
 */
const contextMenuOverlayFeatureSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

/**
 * contextMenuZoneSchema is the zone the right-click context menu shows when the
 * click lands inside one: the zone `id`, its severity `level` (a plain string so
 * an unknown backend level still renders), the free-text `description`, the
 * optional active-window bounds `startAt`/`endAt` as EPOCH SECONDS (null when a
 * bound is unset), and the owning `organizationId` (null when absent). It mirrors
 * the map package's ContextMenuZone — the map resolves the value from the zone
 * tile feature under the cursor and the context-menu tool sets it here. It is
 * re-declared rather than imported because core is a leaf (it cannot depend on the
 * map package); a compatibility test pins the two shapes together so they cannot
 * drift, the same posture the POI/overlay-feature twins take.
 */
const contextMenuZoneSchema = z.object({
  description: z.string(),
  endAt: z.number().nullable(),
  id: z.string(),
  level: z.string(),
  organizationId: z.string().nullable(),
  startAt: z.number().nullable(),
});

/**
 * contextMenuSchema is the right-click context-menu state: the [lng, lat] under
 * the cursor (always shown as coordinates), the container pixel the popup
 * anchors at, the points-of-interest resolved there (empty when none — the
 * coordinate-only floor), the active overlay's vector features under the cursor
 * (empty when no vector overlay is active), and the zone under the cursor (null
 * when the click did not land inside a zone). Zod-first because the value is
 * shared with the context-menu-open tool input and validated when the tool
 * (driven by a human right-click or an agent) opens the menu; a null slot (menu
 * closed) lives outside the schema as the absence of a value.
 */
export const contextMenuSchema = z.object({
  lngLat: z.tuple([z.number(), z.number()]),
  overlayFeatures: z.array(contextMenuOverlayFeatureSchema),
  point: z.tuple([z.number(), z.number()]),
  pois: z.array(contextMenuPoiSchema),
  // null = the right-click did not land inside a zone; the popup then omits its
  // zone section entirely (no empty header). Optional so an existing caller that
  // sets the slot without a zone (a test, a render-free open) defaults to no zone.
  zone: contextMenuZoneSchema.nullable().optional(),
});

/**
 * ContextMenu is the open right-click context-menu state, derived from
 * contextMenuSchema. The context-menu slot holds this or null (the menu is
 * closed).
 */
export type ContextMenu = z.infer<typeof contextMenuSchema>;

/**
 * ContextMenuZone is the zone shown in the right-click context menu, derived from
 * contextMenuZoneSchema. Re-exported so the popup can type the zone section's
 * props without reaching into the map package (core is the leaf the store schema
 * lives in).
 */
export type ContextMenuZone = z.infer<typeof contextMenuZoneSchema>;

/**
 * studioContentSchema names the studio sidenav body slots that may be open at
 * any moment — the create surface, the import-route stub, the suggest stub, and
 * the zone stub. Only one is open at a time: a setter that opens one clears
 * the others, so the layout reads a single mutually exclusive flag. Modal slots
 * live on a separate axis (see studioModalSchema) so opening a modal does not
 * collapse the sidenav body. Zod-first because the value is shared with the
 * openContent setter, with `null` (the absence of a value) meaning every body
 * flag is off.
 */
export const studioContentSchema = z.enum(["create", "import", "suggest", "zone"]);

/**
 * StudioContent names one of the mutually exclusive studio sidenav body slots,
 * derived from studioContentSchema. The studio body slot holds this or null
 * (no body content is open). Modal slots live on StudioModal.
 */
export type StudioContent = z.infer<typeof studioContentSchema>;

/**
 * studioModalSchema names the studio root-level modal slots that may be open at
 * any moment. The set starts with the new-collection modal and is intentionally
 * a union so an export modal or another root-level dialog can join later
 * without touching the sidenav body slot. Only one modal is open at a time —
 * the same mutual-exclusion rule openContent uses — and a null slot (modal
 * closed) lives outside the schema as the absence of a value.
 */
export const studioModalSchema = z.enum(["newCollection"]);

/**
 * StudioModal names one of the mutually exclusive studio modal slots, derived
 * from studioModalSchema. The modal slot holds this or null (no modal is open).
 */
export type StudioModal = z.infer<typeof studioModalSchema>;

/**
 * AgentHandoff names the ONE itinerary entity an agent run has proposed and handed
 * back — the live draft its editPropose opened and deliberately left armed for the
 * user to adjust and save. It is the narrow counterpart to the accumulating
 * {@link ChromeSliceState.agentEditLocks}: that set says "the run touched this", which
 * is the wrong question for a control that must stay usable, because the run touches
 * an entity both when it hands it over AND when it is midway through editing it
 * itself. Only the hand-off verbs raise this slot, so it answers the question the run
 * lock actually asks — "is this draft mine to save?" — and at most one authoring
 * engine can be armed at a time, so at most one entity is ever handed over.
 */
export interface AgentHandoff {
  id: string;
  kind: CollabLockKind;
}

/**
 * ChromeSliceState is the studio chrome: the floating panels, the sidenav body
 * and modal slots, the prompt panel state, the map alert, the right-click
 * context menu, the geolocation prompt + locating flags, the open Street View,
 * the mirrored agent-driving lock, and the parked cross-entity edit switch.
 */
export interface ChromeSliceState {
  /**
   * agentDriving is true while an agent run drives the workspace, mirrored here
   * from the application's agent-threads store so surfaces OUTSIDE that
   * provider — the app shell's sidenav, the routed Studio region — can read one
   * lock flag and go `inert`. It is transient workspace-lock state, parallel to
   * the base-map lock the editor sessions raise (selectBaseMapLocked): owned by
   * the agent shell, written through the bus-free setAgentDriving setter, and
   * reset to false when the shell unmounts. The agent-threads store stays the
   * source of truth (selectAgentDriving); this is the readable mirror for the
   * regions that cannot reach it.
   */
  agentDriving: boolean;
  /**
   * agentEditLocks marks the itinerary entities the ACTIVE agent run has touched,
   * keyed by {@link collabLockKey}(kind, id) → true, so a row can show the run is
   * editing it — the co-pilot analogue of a peer's soft-lock, held by the agent
   * shell rather than a remote collaborator. Locks ACCUMULATE across the run: a
   * touched segment/waypoint stays locked until the run yields the workspace, even
   * after the agent moves on, so the UI reads stable (the agent may return to an
   * entity, and a lock flickering off mid-run would jitter the row). The provider
   * sweeps the set when {@link agentDriving} flips false — the one place a run's
   * touched-entity picture resets — sparing only an entity the run left in an OPEN
   * edit session, whose draft genuinely outlives the run that opened it (see
   * {@link ChromeSliceActions.agentClearEditLocks}). Seeded empty (no entity touched).
   */
  agentEditLocks: Record<string, true>;
  /**
   * agentHandoff is the entity an editPropose left armed for the user to finish, or
   * null (nothing is handed over). It is what the run lock's third carve-out keys on:
   * while it is set, Save and the editing keymap stay live for THAT entity alone,
   * because a draft opened for the user and then locked away is a dead end — no save,
   * no cancel, no Escape. Every other surface stays inert. Raised by the two editPropose
   * hand-off verbs REGARDLESS of who dispatched them, since a human suggestion pick runs
   * the same chain and then starts the very run that would freeze its draft. It cannot
   * leak into an unrelated run because the carve-out is the conjunction of this slot and
   * the armed engine ({@link selectAgentHandoffArmed}), and every leave site lowers it.
   * Lowered when the run takes the entity back or the session ends. Seeded null.
   */
  agentHandoff: AgentHandoff | null;
  /**
   * armSegmentOnActivate is a one-shot intent: the next itinerary that activates
   * should immediately arm its first directions segment's edit, so the points are
   * draggable with no extra click. It is the seam a flow sets when it wants a freshly-
   * activated itinerary to land straight in a segment edit; no flow raises it today, so
   * the activate path reads it false and arms nothing. It is transient cross-step
   * UI state, not part of any flow's draft session, so it lives here with the
   * other one-shot chrome flags; the activate path reads it once and clears it.
   * Seeded false (no auto-arm pending).
   */
  armSegmentOnActivate: boolean;
  contextMenu: ContextMenu | null;
  geolocatePromptOpen: boolean;
  locating: boolean;
  mapAlert: MapAlert | null;
  /**
   * openContent is the studio sidenav body slot the layout shows, or null (the
   * grouped tree). The values are mutually exclusive — opening one (the create
   * surface, the import-route stub, the suggest stub, the zone stub) clears
   * any prior. A single flag (rather than four booleans) is the encoded
   * invariant: the layout reads one value and renders the matching body, so
   * two flags can never be true at once. Modal slots live on openModal so a
   * modal can open without collapsing the sidenav chrome.
   */
  openContent: StudioContent | null;
  /**
   * openModal is the studio root-level modal slot the layout shows, or null
   * (no modal is open). Modal slots are mutually exclusive among themselves
   * the same way openContent values are, and they are decoupled from
   * openContent so a modal opens without collapsing the sidenav body or
   * chrome.
   */
  openModal: StudioModal | null;
  openPanel: StudioPanel | null;
  /**
   * profileSheetSnap is how far the docked elevation profile is opened on a compact
   * viewport, where it is a drag-to-resize bottom sheet. It is inert on a desktop
   * layout, which renders the fixed dock and reveals the analysis on hover. Defaults
   * to `half` — the chart is the profile's headline, and a viewer who wants the
   * analysis or the map back is one drag away in either direction.
   */
  profileSheetSnap: ProfileSheetSnap;
  streetview: StreetviewPosition | null;
}

/**
 * ChromeSliceActions are the whole-slot setters that drive the studio chrome,
 * reached only through the tool bus (except setAgentDriving — see its note).
 */
export interface ChromeSliceActions {
  /**
   * agentClearEditLocks drops every agent edit-lock in one sweep, called by the
   * agent shell when a run yields the workspace (agentDriving flips false) so a
   * touched-entity picture never outlives its run. A no-op when nothing is locked,
   * preserving the map reference so an idle clear does not churn a subscriber's
   * equality check.
   *
   * `retain` spares ONE entity from the sweep: the segment or waypoint the run left
   * in an OPEN edit session, handed to the user to adjust and save. That draft
   * outlives the run that opened it, so clearing its badge on the flip would drop the
   * co-pilot marker off the very row the user was just handed — the one row where it
   * is still true. At most one authoring engine can be armed at a time, so at most
   * one entity is ever spared. Pass nothing to sweep everything (the unmount path,
   * where the session is being torn down anyway).
   */
  agentClearEditLocks: (retain?: { id: string; kind: CollabLockKind } | null) => void;
  /**
   * agentLockEntity records that the active agent run is editing one itinerary
   * entity (a segment or waypoint), so its row shows the co-pilot's editing badge
   * and turns inert. A no-op when the entity is already locked — the equality guard
   * keeps a repeated tool call on the same entity from churning row subscribers.
   * Written only by the agent shell (through ctx.ui, not the tool bus), the sole
   * writer, the same posture as {@link setAgentDriving}.
   */
  agentLockEntity: (kind: CollabLockKind, id: string) => void;
  /**
   * setAgentDriving mirrors the agent-threads run state into the shared UI
   * store so the app shell can lock. Unlike every other UiAction, it is NOT
   * reached through the tool bus: it carries presentation-lock state owned by
   * the agent shell (which lives above the bus, like the threads store
   * itself), not a workspace mutation a human or the agent dispatches. The
   * agent-threads provider is the sole writer.
   */
  setAgentDriving: (driving: boolean) => void;
  /**
   * setAgentHandoff records the entity a run has handed back for the user to adjust
   * and save, or clears the slot with null. Raised by the editPropose hand-off verbs
   * once the draft is armed, and lowered whenever the run takes the entity back (any
   * other agent editor call) or the edit session ends. A no-op when the slot already
   * names the same entity, so a repeated propose on one segment does not churn the
   * subscribers reading the carve-out.
   */
  setAgentHandoff: (target: AgentHandoff | null) => void;
  /**
   * setArmSegmentOnActivate raises (or lowers) the one-shot "arm the next
   * activated itinerary's first segment" intent. A commit path sets it true just
   * before persisting the draft; the activate path reads it once and lowers it.
   * Reached only through the tool bus, like every other UI slot.
   */
  setArmSegmentOnActivate: (value: boolean) => void;
  setContextMenu: (contextMenu: ContextMenu | null) => void;
  setGeolocatePromptOpen: (open: boolean) => void;
  setLocating: (locating: boolean) => void;
  setMapAlert: (alert: MapAlert | null) => void;
  /**
   * setOpenContent opens one studio sidenav body slot and clears any prior;
   * passing null closes whichever was open. The mutual-exclusion is
   * encapsulated here so the tool handlers stay one-liners.
   */
  setOpenContent: (content: StudioContent | null) => void;
  /**
   * setOpenModal opens one studio modal slot and clears any prior; passing
   * null closes whichever was open. The mutual-exclusion is encapsulated here
   * so the tool handlers stay one-liners, and the sidenav body is left
   * untouched so the chrome stays visible when a modal opens.
   */
  setOpenModal: (modal: StudioModal | null) => void;
  setOpenPanel: (panel: StudioPanel | null) => void;
  /**
   * setProfileSheetSnap opens the compact elevation sheet to a snap point. Driven by
   * the sheet's drag handle (release snaps to the nearest) and its tap-to-cycle.
   */
  setProfileSheetSnap: (snap: ProfileSheetSnap) => void;
  setStreetview: (position: StreetviewPosition | null) => void;
}

/**
 * EMPTY_AGENT_EDIT_LOCKS is the shared frozen empty lock map a run with no touched
 * entity reads as — so the initial state and a wholesale clear resolve to one
 * reference-stable default a subscriber's equality check never churns on. The
 * lock action never mutates it: it spreads the current map into a fresh object.
 */
const EMPTY_AGENT_EDIT_LOCKS: Record<string, true> = Object.freeze({});

/**
 * chromeInitialState seeds every chrome slot closed/off.
 */
export const chromeInitialState: ChromeSliceState = {
  agentDriving: false,
  agentEditLocks: EMPTY_AGENT_EDIT_LOCKS,
  agentHandoff: null,
  armSegmentOnActivate: false,
  contextMenu: null,
  geolocatePromptOpen: false,
  locating: false,
  mapAlert: null,
  openContent: null,
  openModal: null,
  openPanel: null,
  profileSheetSnap: "half",
  streetview: null,
};

/**
 * createChromeSlice builds the studio-chrome whole-slot setters. The
 * mutual-exclusion for the panel/content/modal axes lives in their setters so
 * the tool handlers stay one-liners.
 */
export const createChromeSlice: StateCreator<
  ChromeSliceState & ChromeSliceActions,
  [],
  [],
  ChromeSliceActions
> = (set) => ({
  agentClearEditLocks: (retain) =>
    set((state) => {
      const keys = Object.keys(state.agentEditLocks);
      if (keys.length === 0) {
        return {};
      }

      const spared = retain ? collabLockKey(retain.kind, retain.id) : null;
      if (spared === null || !state.agentEditLocks[spared]) {
        return { agentEditLocks: EMPTY_AGENT_EDIT_LOCKS };
      }

      // The spared entity is the only lock left, so the map is already what a sweep
      // would produce — return the same reference rather than an equal copy, so a
      // subscriber's equality check does not churn on a no-op sweep.
      if (keys.length === 1) {
        return {};
      }

      return { agentEditLocks: { [spared]: true } };
    }),
  agentLockEntity: (kind, id) =>
    set((state) => {
      const key = collabLockKey(kind, id);
      if (state.agentEditLocks[key]) {
        return {};
      }

      return { agentEditLocks: { ...state.agentEditLocks, [key]: true } };
    }),
  setAgentDriving: (agentDriving) => set({ agentDriving }),
  setAgentHandoff: (agentHandoff) =>
    set((state) => {
      const current = state.agentHandoff;
      if (current === agentHandoff) {
        return {};
      }

      if (
        current &&
        agentHandoff &&
        current.id === agentHandoff.id &&
        current.kind === agentHandoff.kind
      ) {
        return {};
      }

      return { agentHandoff };
    }),
  setArmSegmentOnActivate: (armSegmentOnActivate) => set({ armSegmentOnActivate }),
  setContextMenu: (contextMenu) => set({ contextMenu }),
  setGeolocatePromptOpen: (geolocatePromptOpen) => set({ geolocatePromptOpen }),
  setLocating: (locating) => set({ locating }),
  setMapAlert: (mapAlert) => set({ mapAlert }),
  setOpenContent: (openContent) => set({ openContent }),
  setOpenModal: (openModal) => set({ openModal }),
  setOpenPanel: (openPanel) => set({ openPanel }),
  setProfileSheetSnap: (profileSheetSnap) => set({ profileSheetSnap }),
  setStreetview: (streetview) => set({ streetview }),
});

/**
 * selectAgentEntityLocked reports whether the active agent run holds an edit-lock
 * on one itinerary entity (a segment or waypoint), so a row renders the co-pilot's
 * editing badge and turns inert. Narrow by design — it keys on THIS entity's slot,
 * so a lock landing on a different entity never re-renders the row — and it mirrors
 * {@link selectCollabLockHolder}'s factory shape so a row reads the agent lock the
 * same way it reads a peer lock. Takes {@link CollabLockKind} for parity with the
 * collab lock key even though only segment/waypoint rows read it.
 */
export const selectAgentEntityLocked =
  (kind: CollabLockKind, id: string) =>
  (state: ChromeSliceState): boolean =>
    state.agentEditLocks[collabLockKey(kind, id)] === true;
